import React, { useEffect, useState } from 'react';
import { AppAlert, AppInputNumber, AppModal, AppSpin } from '@/components/atoms';
import { PRINT_POSITION_UPDATE_ERROR_MESSAGE, usePrintPosition } from '@/hooks/main/usePrintPosition';
import type { PrintPositionItem } from '@/services/main/rezept/printPositionService';
import useAppModal from '@/hooks/useAppModal';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';

// BD field #11: confirm message shown when clicking 適用, before the real save.
const APPLY_CONFIRM_MESSAGE =
  '調整値を変更した場合、未印刷に戻してから再印刷してください。\n（未印刷に戻さないと反映されません。）';

// HIT-4280: only position_type=1 (レセプト) is editable. type=2 (医療助成レセプト 旧用紙：R関)
// was retired by the client and removed from the UI per BD.
const EDITABLE_POSITION_TYPE = 1;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type FieldErrors = Record<string, string>;

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
    };
  };
}

/** Flatten Laravel-style errors `{ "positions.0.left": ["msg"] }` → `{ key: "msg" }`. */
function flattenFieldErrors(error: unknown): FieldErrors {
  const errors = (error as AxiosLikeError)?.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return {};
  const out: FieldErrors = {};
  for (const [key, messages] of Object.entries(errors)) {
    if (Array.isArray(messages) && messages[0]) out[key] = messages[0];
  }
  return out;
}

const PrintAdjustModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const toast = useAppToast();
  const appModal = useAppModal();
  const { positions, isLoading, isSaving, save, refetch } = usePrintPosition(isOpen);
  const [form, setForm] = useState<PrintPositionItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (isOpen) {
      refetch();
      setErrorMessage(null);
      setFieldErrors({});
    }
  }, [isOpen, refetch]);

  useEffect(() => {
    if (positions.length > 0) setForm(positions);
  }, [positions]);

  const editable = form.find(p => p.position_type === EDITABLE_POSITION_TYPE);
  const editableIndex = form.findIndex(p => p.position_type === EDITABLE_POSITION_TYPE);

  const handleChange = (field: 'top' | 'left', value: number | null) => {
    setForm(prev =>
      prev.map(p =>
        p.position_type === EDITABLE_POSITION_TYPE ? { ...p, [field]: value ?? 0 } : p
      )
    );
    if (editableIndex >= 0) {
      const key = `positions.${editableIndex}.${field}`;
      if (fieldErrors[key]) {
        setFieldErrors(prev => {
          const { [key]: _omit, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const doSave = async () => {
    try {
      // HIT-4280: only send the editable position to BE; type=2 is retired.
      const payload = form.filter(p => p.position_type === EDITABLE_POSITION_TYPE);
      await save(payload);
      setErrorMessage(null);
      setFieldErrors({});
      toast.success('更新が正常に終了しました。');
      onClose();
    } catch (error) {
      const status = (error as AxiosLikeError)?.response?.status;
      setErrorMessage(getApiErrorMessage(error, PRINT_POSITION_UPDATE_ERROR_MESSAGE));
      if (status === 422) {
        setFieldErrors(flattenFieldErrors(error));
      } else {
        setFieldErrors({});
        setForm(positions);
      }
    }
  };

  const handleApply = () => {
    appModal.confirm({
      type: 'warning',
      content: <span className="whitespace-pre-line">{APPLY_CONFIRM_MESSAGE}</span>,
      onOk: async () => {
        await doSave();
      },
    });
  };

  const topError = editableIndex >= 0 ? fieldErrors[`positions.${editableIndex}.top`] : undefined;
  const leftError = editableIndex >= 0 ? fieldErrors[`positions.${editableIndex}.left`] : undefined;

  return (
    <AppModal
      isOpen={isOpen}
      title="印字位置調整"
      onClose={onClose}
      maxWidth={600}
      onOk={handleApply}
      okText="適用"
      confirmLoading={isSaving}
    >
      {isLoading ? (
        <div className="flex justify-center py-8"><AppSpin /></div>
      ) : (
        <div className="space-y-6">
          {errorMessage && (
            <AppAlert type="error" message={errorMessage} showIcon />
          )}

          {/* HIT-4280 §1 — 縦方向調整 (vertical = top offset) */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold">縦方向調整</span>
              <AppInputNumber
                step={0.1}
                min={-10}
                max={10}
                precision={1}
                value={editable?.top ?? 0}
                onChange={v => handleChange('top', v as number | null)}
                className="w-24"
                status={topError ? 'error' : undefined}
              />
              <span>mm</span>
            </div>
            {topError && <div className="text-xs text-red-600 mb-1">{topError}</div>}
            <div className="text-sm text-gray-700 leading-relaxed ml-1">
              <div>上に印字を移動させる場合は、マイナスの数値を入力してください。</div>
              <div>下に印字を移動させる場合は、プラスの数値を入力してください。</div>
              <div>※ ミリ単位（小数点一桁）まで入力可能です。</div>
            </div>
          </div>

          <hr className="border-t border-gray-200" />

          {/* HIT-4280 §2 — 横方向調整 (horizontal = left offset) */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold">横方向調整</span>
              <AppInputNumber
                step={0.1}
                min={-10}
                max={10}
                precision={1}
                value={editable?.left ?? 0}
                onChange={v => handleChange('left', v as number | null)}
                className="w-24"
                status={leftError ? 'error' : undefined}
              />
              <span>mm</span>
            </div>
            {leftError && <div className="text-xs text-red-600 mb-1">{leftError}</div>}
            <div className="text-sm text-gray-700 leading-relaxed ml-1">
              <div>左に印字を移動させる場合は、マイナスの数値を入力してください。</div>
              <div>右に印字を移動させる場合は、プラスの数値を入力してください。</div>
              <div>※ ミリ単位（小数点一桁）まで入力可能です。</div>
            </div>
          </div>
        </div>
      )}
    </AppModal>
  );
};

export default PrintAdjustModal;
